/* eslint-disable no-restricted-globals */
import { useEffect, useState } from "react";
import api from "services/api";
import { useGroupStore } from "stores/useGroup";
import { useUser } from "stores/useUser";
import { useNavigate } from "react-router-dom";
import { MainLayoutGroupPage } from "./mainLayout";
import { useTranslation } from "react-i18next";
import { Report, ReportState, ReportType } from "models/report";
import dayjs from "dayjs";
import { Ban, Check, Clock, DownloadIcon, FileAudio2, FileText, FileVideo2, HelpCircle, LineChartIcon, Loader2, MessageSquare, TrashIcon, UploadIcon } from "lucide-react";
import { Button } from "components/Button";
import { useContextMenu } from "stores/useContextMenu";
import { ReportDialog } from "./ReportDialog";
import { useWs } from "stores/useWs";

interface ReportListItemProps {
  id: number,
  guid: string,
  onSelect: (report: Report) => void,
  onRemove: (report: Report) => void,
  onDownload: (e: React.MouseEvent<HTMLButtonElement>, report: Report) => void,
  once: (key: string, callback: any) => void,
}
function ReportListItem({ once, id, guid, onSelect, onRemove, onDownload }: ReportListItemProps) {
  const [report, setReport] = useState<Report | null>(null);
  const group = useGroupStore(state => state.group);
  const { t } = useTranslation()

  function refresh() {
    api.get(`/groups/${group?.id}/reports/${id}`)
      .then(res => setReport(res.data))
  }

  useEffect(() => {
    refresh();
    once(guid + ".update", refresh);
  }, [guid]);

  if (report === null)
    return <tr key={id} className="hover:bg-zinc-100 group">
      <Loader2 className="animate-spin" />
    </tr>;

  return (
  <tr key={report.id} className="hover:bg-zinc-100 group">
    <td className="p-3 align-middle">
      <img onClick={(_) => onSelect(report)} className="hover:cursor-pointer w-32 m-0" src={`${api.defaults.baseURL}/../../reports/${report.guid}.png`} alt="" />
    </td>
    <td className="p-3 align-middle">
      <div className="flex flex-col">
        <span className="text-xs text-zinc-400">{t("reports.type")}</span>
        {t("reportType." + ReportType[report.type])}
      </div>
    </td>
    <td className="p-3 align-middle">
      <div className="flex flex-col">
        <span className="text-xs text-zinc-400">{t("patients.listRegistration")}</span>
        {dayjs(report.createdAt).format(t("time.full"))}
      </div>
    </td>
    
    {report.state !== ReportState.done && <td className="p-3 align-middle">
      <div className="flex flex-col">
        <span className="text-xs text-zinc-400">{t("reports.state")}</span>
        <div className="flex items-center gap-2">
          {
            (report.state === ReportState.none || report.state === ReportState.pending) && <Clock className="text-zinc-400" size={16} />
          }
          {
            (report.state === ReportState.extractingAudio || report.state === ReportState.transcripting || report.state === ReportState.extractingInformations) && <div className="relative h-2 w-2">
              <span className="bg-green-500 h-2 w-2 rounded-full absolute left-0 top-0" />
              <span className="bg-green-500 h-2 w-2 rounded-full animate-ping absolute" />
            </div>
          }
          {
            (report.state === ReportState.error) && <Ban className="text-red-500" size={16} />
          }
          {t("reportState." + ReportState[report.state])}
          {
            (report.state === 8) && 
            <div className="group/error relative">
              <HelpCircle className="text-zinc-400 group-hover/error:text-black" size={16} />
              <div className="hidden group-hover/error:flex absolute w-64 top-6 right-0 rounded-t-md border border-zinc-200 bg-white overflow-hidden drop-shadow-md">
                <p className="w-full text-wrap m-0 p-4">{t("reports.errorCode")} {report.notes}</p>
              </div>
            </div>
          }
        </div>
      </div>
    </td>}
    {report.state === ReportState.done && <td className="p-3 align-middle">
      <div className="flex flex-col">
        <span className="text-xs text-zinc-400">{t("reports.notes")}</span>
        {report.notes}
      </div>
    </td>}
    <td className="p-3 align-middle text-right">
      {report.state === ReportState.done && <div className="invisible group-hover:visible">
        <button
          onClick={(_) => onSelect(report)}
          className="p-2 rounded hover:bg-zinc-200 text-zinc-800"
        >
          <LineChartIcon size={20} />
        </button>
        <button
          onClick={(e) => onDownload(e, report)}
          className="p-2 rounded hover:bg-zinc-200 text-zinc-800"
        >
          <DownloadIcon size={20} />
        </button>
        <button
          onClick={(_) => onRemove(report)}
          className="p-2 rounded text-zinc-800 hover:bg-red-200 hover:text-red-500"
        >
          <TrashIcon size={20} />
        </button>
      </div>}
      {report.state === ReportState.error && <div className="invisible group-hover:visible">
        <button
          onClick={(_) => onRemove(report)}
          className="p-2 rounded text-zinc-800 hover:bg-red-200 hover:text-red-500"
        >
          <TrashIcon size={20} />
        </button>
      </div>}
    </td>
  </tr>)

}
export function GroupReportPage() {
  const { once } = useWs();
  const openMenu = useContextMenu((state) => state.open);
  const group = useGroupStore(state => state.group);
  const user = useUser(state => state.user);
  const can = useUser(state => state.can);
  const [reports, setReports] = useState<Report[]>([]);
  const [reportDialog, setReportDialog] = useState(false);
  const navigate = useNavigate();
  const { t } = useTranslation()

  function handleUser() {
    if (user) {
      if (can(["admin"]) === false) {
        if (!user.group) {
          navigate(`/`);
          return;
        }
        navigate(`/grupos/${user?.group.id}`);
        return;
      }
    }
  }
  useEffect(handleUser, [user]);
  function reloadReports() {
    if (group) {
      api.get(`/groups/${group.id}/reports`).then((res) => setReports(res.data));
    }
  }
  useEffect(reloadReports, []);

  function handleNewReport() {
    setReportDialog(true);
  }
  function onSelect(report: Report) {
    if (report.state === ReportState.done)
      navigate(`/grupos/${group?.id}/relatorios/${report.id}`)
  }
  function handleDownload(report: Report, format: string) {
    api.get(`/groups/${group?.id}/reports/file/${report.guid}${format}`, { responseType: 'blob' })
      .then((response) => {
        // create file link in browser's memory
        const href = URL.createObjectURL(response.data);

        // create "a" HTML element with href to file & click
        const link = document.createElement('a');
        link.href = href;
        link.setAttribute('download', `relatorio-${dayjs(report.createdAt).format("DD-MM-YYYY-HH-mm")}${format}`); //or any other extension
        document.body.appendChild(link);
        link.click();

        // clean up "a" element & remove ObjectURL
        document.body.removeChild(link);
        URL.revokeObjectURL(href);
      });
  }
  function handleClickDownload(e: React.MouseEvent<HTMLButtonElement>, report: Report) {
    e.preventDefault();
    openMenu({ x: e.pageX, y: e.pageY }, [
      {
        title: "Vídeo",
        onClick: () => handleDownload(report, ".mp4"),
        icon: FileVideo2
      },
      {
        title: "Áudio",
        onClick: () => handleDownload(report, ".mp3"),
        icon: FileAudio2
      },
      {
        title: "Transcrição",
        onClick: () => handleDownload(report, ".txt"),
        icon: FileText
      },
      {
        title: "Legenda",
        onClick: () => handleDownload(report, ".vtt"),
        icon: MessageSquare
      }
    ])
  }
  function onRemove(report: Report) {
    if (
      confirm(t("reports.deleteAlert"))
    ) {
      api.delete(`/groups/${group?.id}/reports/${report.id}`)
        .then(reloadReports);
    }
  }

  if (group === null)
    return <></>;

  return (
    <MainLayoutGroupPage
      title={t("reports.title")}
      subtitle={t("reports.description")}
      actionButton={
        <Button onClick={handleNewReport}>
          <UploadIcon size={16} />
          {t("reports.new")}
        </Button>
      }>
      <table className="m-0">
        <tbody>
          {reports.map((report) => <ReportListItem once={once} id={report.id} guid={report.guid} onDownload={handleClickDownload} onRemove={onRemove} onSelect={onSelect} />)}
        </tbody>
      </table>
      <ReportDialog
        open={reportDialog === true}
        setOpen={setReportDialog}
        group={group}
        onSubmit={reloadReports}
      />
    </MainLayoutGroupPage>
  );
}
